import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Reusable JSON-LD injector.
 *
 * Why this exists instead of inlining `<script type="application/ld+json">`
 * directly in each page:
 *
 *   1. `react-helmet-async` makes head-tag updates deterministic across
 *      client-side navigations — without it, navigating from /faq to /
 *      would leave the previous page's schema in the DOM.
 *
 *   2. The JSON has to survive `</script>` substrings inside string
 *      content (e.g. an article excerpt that mentions "</script>").
 *      We escape the slash so browsers don't break out of the block.
 *
 *   3. Centralising the component gives us one place to add structural
 *      tweaks (e.g. injecting `@id` cross-references) later without
 *      hunting through every consumer.
 *
 * Usage:
 *   <JsonLd data={{ "@context": "https://schema.org", "@type": "Organization", ... }} />
 *
 * For multiple unrelated schemas on one page, render multiple <JsonLd>
 * components. They each emit their own <script> block — that's the
 * recommended pattern from schema.org and Google.
 */
interface JsonLdProps {
  data: Record<string, unknown> | Record<string, unknown>[];
  // Optional id — useful when a test or analytics hook wants to target
  // a specific schema block by `document.getElementById`.
  id?: string;
}

export const JsonLd: React.FC<JsonLdProps> = ({ data, id }) => {
  // Always escape `</script` — JSON.stringify happily produces it
  // verbatim and HTML parsers will close the script element early.
  const payload = JSON.stringify(data).replace(/<\/script/gi, '<\\/script');
  return (
    <Helmet>
      <script type="application/ld+json" {...(id ? { 'data-jsonld-id': id } : {})}>
        {payload}
      </script>
    </Helmet>
  );
};

export default JsonLd;
